"use client";

import Image from "next/image";
import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useOutsideClick } from "@/hooks/use-outside-click";
import { platformIcons, platformNames } from "@/utils/platform-specific-data";
import { addAffiliateParams } from "@/utils/affiliate-links";
import { slugifyReleaseName } from "@/lib/artistUtils";
import { ArrowRight } from "lucide-react";
import { FaChevronRight } from "./icons";
import Link from "next/link";

interface Props {
  cards: {
    id: string;
    artistHandle: string;
    short_id: string;
    description: string;
    title: string;
    duration: string;
    src: string;
    ctaText: string;
    links: {
      platform:
        | "spotify"
        | "soundcloud"
        | "beatport"
        | "youtube"
        | "apple_music";
      url: string;
    }[];
    content: React.ReactNode | (() => React.ReactNode);
  }[];
}

export default function TrackItemsGrid({ cards }: Props) {
  const [active, setActive] = useState<(typeof cards)[number] | boolean | null>(
    null
  );
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setActive(false);
      }
    }

    if (active && typeof active === "object") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);

  useOutsideClick(ref, () => setActive(null));

  const handleModalOpen = (
    e: React.MouseEvent,
    card: (typeof cards)[number]
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setActive(card);
  };

  return (
    <>
      <AnimatePresence>
        {active && typeof active === "object" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-overlay/20 h-full w-full z-10"
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {active && typeof active === "object" ? (
          <div className="fixed inset-0 grid place-items-center z-[100]">
            <motion.button
              key={`button-${active.title}-${id}`}
              layout
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
                transition: {
                  duration: 0.05,
                },
              }}
              className="flex absolute top-2 right-2 lg:hidden items-center justify-center bg-theme-neon border-1 border-line-strong rounded-full h-6 w-6"
              onClick={() => setActive(null)}
            >
              <CloseIcon />
            </motion.button>
            <motion.div
              layoutId={`card-${active.title}-${id}`}
              ref={ref}
              className="w-full max-w-[500px] h-full md:h-fit md:max-h-[90%] flex flex-col bg-surface-raised dark:bg-surface sm:rounded-3xl overflow-hidden"
            >
              <motion.div layoutId={`image-${active.title}-${id}`}>
                <Image
                  priority
                  width={1000}
                  height={1000}
                  src={active.src}
                  alt={active.title}
                  className="w-full h-80 sm:rounded-tr-lg sm:rounded-tl-lg object-cover object-top"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 33vw, 33vw"
                />
              </motion.div>

              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="">
                    <motion.h3
                      layoutId={`title-${active.title}-${id}`}
                      className="text-sm hover:underline cursor-pointer"
                    >
                      {active.title}
                    </motion.h3>
                    {/* <motion.p
                            layoutId={`description-${active.description}-${active.title}-${id}`}
                            className="text-xs text-muted hover:underline cursor-pointer"
                          >
                            {active.description}
                          </motion.p> */}
                  </div>

                  <motion.a
                    layoutId={`button-${active.title}-${id}`}
                    href={`/track/${slugifyReleaseName(active.title)}/${active.short_id}`}
                    className="px-2 py-1 rounded-full font-bold bg-theme-neon text-on-accent text-xs"
                  >
                    {active.ctaText}
                  </motion.a>
                </div>

                <div className="relative">
                  <motion.div
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-subtle text-xs py-4 flex flex-col items-start ga overflow-auto dark:text-muted [scrollbar-width:none] [-ms-overflow-style:none] [-webkit-overflow-scrolling:touch]"
                  >
                    {typeof active.content === "function"
                      ? active.content()
                      : active.content}
                  </motion.div>
                </div>

                <div className="flex flex-col items-center justify-center gap-4">
                  {active.links
                    .sort((a, b) => {
                      // Beatport always first
                      if (a.platform === "beatport") return -1;
                      if (b.platform === "beatport") return 1;
                      // Then sort alphabetically
                      return platformNames[a.platform].localeCompare(
                        platformNames[b.platform]
                      );
                    })
                    .map((link) => (
                      <motion.a
                        key={`${link.platform}-${link.url}`}
                        layoutId={`button-${link.platform}-${link.url}-${active.title}-${id}`}
                        href={addAffiliateParams(link.url, link.platform)}
                        target="_blank"
                        rel={
                          link.platform === "beatport"
                            ? "nofollow sponsored"
                            : undefined
                        }
                        className="h-10 w-full flex space-x-2 text-center items-center justify-center px-2 py-1 rounded-md font-bold bg-theme-neon text-on-accent text-xs"
                      >
                        {platformIcons[link.platform]}
                        <span>{platformNames[link.platform]}</span>
                      </motion.a>
                    ))}
                </div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>
      {cards.map((card, index) => (
        <motion.div
          layoutId={`card-${card.title}-${id}`}
          key={`card-${card.title}-${id}`}
          onClick={() => setActive(card)}
          className="col-span-1 flex flex-col items-center group overflow-hidden rounded-md relative cursor-pointer"
        >
          {/* Link to track page */}
          <Link
            href={`/track/${slugifyReleaseName(card.title)}/${card.short_id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Go to ${card.title} track page`}
            className="absolute top-2 right-2 z-10 bg-theme-neon hover:bg-theme-neon/80 rounded-full p-1 backdrop-blur-sm transition-colors duration-200"
          >
            <FaChevronRight size={20} className="mt-[1px] text-on-accent" />
          </Link>

          <div className="w-full cursor-pointer">
            <motion.div
              className="relative w-full aspect-square border-ink/20 border-[1px] rounded-md overflow-hidden"
              layoutId={`image-${card.title}-${id}`}
            >
              <Image
                src={card.src}
                alt={card.title}
                fill
                className="object-cover transition duration-300 ease-in-out group-hover:scale-105"
                sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 16vw"
                priority={index < 4}
              />
            </motion.div>

            <motion.h3
              layoutId={`title-${card.title}-${id}`}
              className="mt-2 text-sm font-bold"
            >
              {card.title}
            </motion.h3>
            <motion.p className="text-xs text-muted">
              {card.description}
            </motion.p>
          </div>
        </motion.div>
      ))}
    </>
  );
}

export const CloseIcon = () => {
  return (
    <motion.svg
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.05,
        },
      }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M18 6l-12 12" />
      <path d="M6 6l12 12" />
    </motion.svg>
  );
};
